import {useEffect} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {
  envIsIntegration,
  envIsServerDeployment,
  serverEnableBackendGrouping,
} from '../../../config';
import {RampIdType} from '../../../generated/graphql';
import {useRecordFeatureFlag} from '../../datadog/recordFeatureFlag';
import {
  useEntityFlagFromStore,
  useOrgFlagFromStore,
  useUserFlagFromStore,
  useUserOrOrgFlagFromStore,
} from './useRampFlagStore';

/**
 * Store-backed versions of the line plot flag hooks. Each function keeps the
 * original env checks, fallbacks, and return value, but reads from the current
 * route's preloaded flag store. Datadog logging stays beside the flag decision;
 * the shared hook skips repeated identical values.
 *
 * The entity flag store is loaded from the current route's project, so callers
 * no longer need to pass `entityName`.
 *
 * The originals in `linePlotFeatureFlags.ts` are still in place; migrate
 * call sites incrementally by swapping imports.
 */

/**
 * Caches `useViewerFeatureFlagsQuery` results as O(1) rampKey -> boolean maps
 * (pre-evaluated at init time, one map per rampIDType). Readers do a single
 * Record lookup and no Datadog work.
 */
type LinePlotFlagsState = {
  isBackendSmoothingEnabled: boolean;
  isBackendGroupingEnabled: boolean;
  isLineConstructionProfilingEnabled: boolean;
  // Tri-state preserved for `?? true`/`?? false` call sites.
  isFullFidelityRangesEnabled: boolean | undefined;
  isFullFidelityRunsPerChartEnabled: boolean;
  isIncreaseFullScreenMaxRunsEnabled: boolean;
  isDynamicFullScreenBinSizingEnabled: boolean;
  isBackendLineConstructionEnabled: boolean;
  isBackendLineConstructionGroupedEnabled: boolean;
  isBackendLineConstructionSmoothingEnabled: boolean;
  isBackendLineConstructionReportsEnabled: boolean;
  isClientSideDynamicTimeoutsEnabled: boolean;
  isColumnarBucketedEnabled: boolean;
  isBucketedQuerySourceEnabled: boolean;
};

const INITIAL_LINE_PLOT_FLAGS_STATE: LinePlotFlagsState = {
  isBackendSmoothingEnabled: false,
  isBackendGroupingEnabled: false,
  isLineConstructionProfilingEnabled: false,
  isFullFidelityRangesEnabled: undefined,
  isFullFidelityRunsPerChartEnabled: false,
  isIncreaseFullScreenMaxRunsEnabled: false,
  isDynamicFullScreenBinSizingEnabled: false,
  isBackendLineConstructionEnabled: false,
  isBackendLineConstructionGroupedEnabled: false,
  isBackendLineConstructionSmoothingEnabled: false,
  isBackendLineConstructionReportsEnabled: false,
  isClientSideDynamicTimeoutsEnabled: false,
  isColumnarBucketedEnabled: false,
  isBucketedQuerySourceEnabled: false,
};

export const useLinePlotFlagsStore = create<LinePlotFlagsState>()(
  devtools(() => INITIAL_LINE_PLOT_FLAGS_STATE, {name: 'LinePlotFlagsStore'})
);

export const useInitializeLinePlotFlagsStore = () => {
  const isBackendSmoothingEnabled = useRampFlagUseBackendSmoothing();
  const isBackendGroupingEnabled = useEnableBackendGrouping();
  const isLineConstructionProfilingEnabled =
    useRampFlagLineConstructionProfiling();
  const isFullFidelityRangesEnabled = useRampFlagFullFidelityRanges();
  const isFullFidelityRunsPerChartEnabled =
    useRampFlagFullFidelityRunsPerChart();
  const isIncreaseFullScreenMaxRunsEnabled =
    useRampFlagIncreaseFullScreenMaxRuns();
  const isDynamicFullScreenBinSizingEnabled =
    useRampFlagDynamicFullScreenBinSizing();
  const isBackendLineConstructionEnabled =
    useStatsigGateBackendLineConstruction();
  const isBackendLineConstructionGroupedEnabled =
    useStatsigGateBackendLineConstructionGrouped();
  const isBackendLineConstructionSmoothingEnabled =
    useStatsigGateBackendLineConstructionSmoothing();
  const isBackendLineConstructionReportsEnabled =
    useStatsigGateBackendLineConstructionReports();
  const isClientSideDynamicTimeoutsEnabled =
    useRampFlagClientsideDynamicTimeouts();
  const isColumnarBucketedEnabled = useStatsigGateColumnarBucketed();
  const isBucketedQuerySourceEnabled = useStatsigGateBucketedQuerySource();

  // This is a valid Effect: it synchronizes React hook outputs into the
  // external Zustand store after render.
  // https://react.dev/reference/react/useEffect
  useEffect(() => {
    useLinePlotFlagsStore.setState({
      isBackendSmoothingEnabled,
      isBackendGroupingEnabled,
      isLineConstructionProfilingEnabled,
      isFullFidelityRangesEnabled,
      isFullFidelityRunsPerChartEnabled,
      isIncreaseFullScreenMaxRunsEnabled,
      isDynamicFullScreenBinSizingEnabled,
      isBackendLineConstructionEnabled,
      isBackendLineConstructionGroupedEnabled,
      isBackendLineConstructionSmoothingEnabled,
      isBackendLineConstructionReportsEnabled,
      isClientSideDynamicTimeoutsEnabled,
      isColumnarBucketedEnabled,
      isBucketedQuerySourceEnabled,
    });
  }, [
    isBackendSmoothingEnabled,
    isBackendGroupingEnabled,
    isLineConstructionProfilingEnabled,
    isFullFidelityRangesEnabled,
    isFullFidelityRunsPerChartEnabled,
    isIncreaseFullScreenMaxRunsEnabled,
    isDynamicFullScreenBinSizingEnabled,
    isBackendLineConstructionEnabled,
    isBackendLineConstructionGroupedEnabled,
    isBackendLineConstructionSmoothingEnabled,
    isBackendLineConstructionReportsEnabled,
    isClientSideDynamicTimeoutsEnabled,
    isColumnarBucketedEnabled,
    isBucketedQuerySourceEnabled,
  ]);
};

const useRampFlagUseBackendSmoothing = () => {
  const isDeploymentEnabled = useOrgFlagFromStore(
    'backend_smoothing_deployment',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityFlagFromStore(
    'backend_smoothing_entity',
    RampIdType.EntityName
  );
  const value = envIsServerDeployment
    ? (isDeploymentEnabled ?? false)
    : isEntityEnabled;
  useRecordFeatureFlag('backend_smoothing', value);
  return value;
};

const useEnableBackendGrouping = () => {
  const value = serverEnableBackendGrouping();
  useRecordFeatureFlag('use_backend_grouping', value);
  return value;
};

const useRampFlagLineConstructionProfiling = () => {
  return useEntityFlagFromStore(
    'line-construction-profiling',
    RampIdType.EntityName
  );
};

const useRampFlagFullFidelityRanges = () => {
  const isDeploymentEnabled = useOrgFlagFromStore(
    'full_fidelity_ranges_deployment',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityFlagFromStore(
    'full_fidelity_ranges_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserFlagFromStore(
    'full_fidelity_ranges_user',
    RampIdType.UserName
  );

  const isEnabled = envIsServerDeployment
    ? (isDeploymentEnabled ?? true)
    : isEntityEnabled || isUserEnabled;
  useRecordFeatureFlag('full_fidelity_ranges', isEnabled ?? false);
  return isEnabled;
};

/**
 * This currently doesn't support an entity flag because I didn't want to do the work to thread and entity value into the check. It probably won't be that hard but we're just experimenting currently.
 */
const useRampFlagFullFidelityRunsPerChart = () => {
  const value = useUserOrOrgFlagFromStore(
    'full_fidelity_run_per_chart_user',
    'full_fidelity_run_per_chart_org'
  );
  useRecordFeatureFlag('full_fidelity_run_per_chart', value);
  return value;
};

/**
 * User-level feature flag for higher full-screen line plot max runs
 * (backend: increase_full_screen_max_runs_user).
 */
const useRampFlagIncreaseFullScreenMaxRuns = () => {
  const isUserEnabled = useUserFlagFromStore(
    'increase_full_screen_max_runs_user',
    RampIdType.UserId
  );
  const value = isUserEnabled || false;
  useRecordFeatureFlag('increase_full_screen_max_runs', value);
  return value;
};

/**
 * Org-level flag for dynamic full-screen bin sizing.
 * When enabled, the full-screen 10k query uses Math.min(width * 4, 10000)
 * instead of a hardcoded 10,000 bins, matching uPlot's decimation threshold.
 */
const useRampFlagDynamicFullScreenBinSizing = () => {
  const isOrgEnabled = useOrgFlagFromStore(
    'dynamic_full_screen_bin_sizing_org',
    RampIdType.OrgId
  );
  const value = isOrgEnabled || false;
  useRecordFeatureFlag('dynamic_full_screen_bin_sizing', value);
  return value;
};

const useStatsigGateBackendLineConstruction = () => {
  const isUserEnabled = useUserFlagFromStore(
    'backend_line_construction_user',
    RampIdType.UserId
  );
  const isOrgEnabled = useOrgFlagFromStore(
    'backend_line_construction_org',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityFlagFromStore(
    'backend_line_construction_entity',
    RampIdType.EntityId
  );
  const value = isUserEnabled || isOrgEnabled || isEntityEnabled;
  // Master used the literal string 'backend_line_construction_user' here even
  // though the value combines user/org/entity flags - preserve verbatim so
  // existing dashboards keep working.
  useRecordFeatureFlag('backend_line_construction', value);
  return value;
};

const useStatsigGateBackendLineConstructionGrouped = () => {
  const value = useEntityFlagFromStore(
    'backend_line_construction_grouped_entity',
    RampIdType.EntityId
  );
  useRecordFeatureFlag('backend_line_construction_grouped', value);
  return value;
};

const useStatsigGateBackendLineConstructionSmoothing = () => {
  const value = useEntityFlagFromStore(
    'backend_line_construction_smoothing',
    RampIdType.EntityName
  );
  useRecordFeatureFlag('backend_line_construction_smoothing', value);
  return value;
};

const useStatsigGateBackendLineConstructionReports = () => {
  const value = useEntityFlagFromStore(
    'backend_line_construction_reports_entity',
    RampIdType.EntityId
  );
  useRecordFeatureFlag('backend_line_construction_reports', value);
  return value;
};

const useRampFlagClientsideDynamicTimeouts = () => {
  const value = useEntityFlagFromStore(
    'clientside_dynamic_timeouts_entity',
    RampIdType.EntityId
  );
  useRecordFeatureFlag('clientside_dynamic_timeouts_entity', value);
  return value;
};

// Gates the Connect columnar endpoint; integration runs stay on GraphQL.
const useStatsigGateColumnarBucketed = () => {
  const isEntityEnabled = useEntityFlagFromStore(
    'columnar_bucketed_history_entity',
    RampIdType.EntityId
  );
  const value = envIsIntegration ? false : isEntityEnabled;
  useRecordFeatureFlag('columnar_bucketed_history', value);
  return value;
};

// Chooses BucketedQueryManager or QueryStore for line plots. Recorded so RUM
// events carry the cohort.
const useStatsigGateBucketedQuerySource = () => {
  const isOrgEnabled = useOrgFlagFromStore(
    'query_tether_org',
    RampIdType.OrgId
  );
  // Flags that have not loaded yet read as off.
  const value = isOrgEnabled || false;
  useRecordFeatureFlag('bucketed_query_source', value);
  return value;
};
